import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// ============================================================================
// INTERFACES
// ============================================================================

export interface ElearningCartItem {
  course_id: string;
  title: string;
  slug: string;
  price: number;
  currency: string;
  quantity: number; // Toujours 1 pour les cours, mais gardé pour la compatibilité
  thumbnail_url?: string;
  instructor_name?: string;
  instructor_avatar?: string;
  duration_hours?: number;
  difficulty_level?: 'beginner' | 'intermediate' | 'advanced';
  category_name?: string;
  category_color?: string;
}

export interface ElearningCartState {
  items: ElearningCartItem[];
  isLoading: boolean;
  
  // Actions
  addItem: (item: Omit<ElearningCartItem, 'quantity'>) => void;
  removeItem: (courseId: string) => void;
  updateQuantity: (courseId: string, quantity: number) => void;
  clearCart: () => void;
  getItemCount: () => number;
  getCartTotal: () => number;
  isItemInCart: (courseId: string) => boolean;
  getItem: (courseId: string) => ElearningCartItem | undefined;
  
  // Utilitaires
  loadFromLocalStorage: () => void;
  saveToLocalStorage: () => void;
}

// ============================================================================
// STORE ZUSTAND
// ============================================================================

export const useElearningCartStore = create<ElearningCartState>()(
  persist(
    (set, get) => ({
      items: [],
      isLoading: false,

      // Ajouter un article au panier
      addItem: (item) => {
        const { items } = get();
        const existingItem = items.find(cartItem => cartItem.course_id === item.course_id);

        if (existingItem) {
          // Pour les cours, on ne peut pas avoir plusieurs quantités
          // Donc on ne fait rien si le cours est déjà dans le panier
          return;
        }

        const newItem: ElearningCartItem = {
          ...item,
          quantity: 1 // Toujours 1 pour les cours
        };

        set({ items: [...items, newItem] });
      },

      // Supprimer un article du panier
      removeItem: (courseId) => {
        const { items } = get();
        const filteredItems = items.filter(item => item.course_id !== courseId);
        set({ items: filteredItems });
      },

      // Mettre à jour la quantité (pour les cours, cette fonction ne fait rien)
      updateQuantity: (courseId, quantity) => {
        // Pour les cours e-learning, la quantité est toujours 1
        // Cette fonction est gardée pour la compatibilité avec l'interface du shop
        if (quantity < 1) {
          get().removeItem(courseId);
          return;
        }
        
        const { items } = get();
        const updatedItems = items.map(item =>
          item.course_id === courseId ? { ...item, quantity: 1 } : item
        );
        set({ items: updatedItems });
      },

      // Vider le panier
      clearCart: () => {
        set({ items: [] });
      },

      // Obtenir le nombre total d'articles
      getItemCount: () => {
        const { items } = get();
        return items.reduce((total, item) => total + item.quantity, 0);
      },

      // Obtenir le total du panier
      getCartTotal: () => {
        const { items } = get();
        return items.reduce((total, item) => total + (item.price * item.quantity), 0);
      },

      // Vérifier si un article est dans le panier
      isItemInCart: (courseId) => {
        const { items } = get();
        return items.some(item => item.course_id === courseId);
      },

      // Obtenir un article spécifique
      getItem: (courseId) => {
        const { items } = get();
        return items.find(item => item.course_id === courseId);
      },

      // Charger depuis localStorage (fonction utilitaire)
      loadFromLocalStorage: () => {
        try {
          const stored = localStorage.getItem('elearn_cart');
          if (stored) {
            const parsedItems = JSON.parse(stored);
            set({ items: parsedItems });
          }
        } catch (error) {
          console.error('Erreur lors du chargement du panier e-learning:', error);
        }
      },

      // Sauvegarder dans localStorage (fonction utilitaire)
      saveToLocalStorage: () => {
        try {
          const { items } = get();
          localStorage.setItem('elearn_cart', JSON.stringify(items));
        } catch (error) {
          console.error('Erreur lors de la sauvegarde du panier e-learning:', error);
        }
      }
    }),
    {
      name: 'elearn_cart', // Clé dans localStorage
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ items: state.items }), // Ne persister que les items
    }
  )
);

// ============================================================================
// HOOKS UTILITAIRES
// ============================================================================

/**
 * Hook pour obtenir les statistiques du panier
 */
export const useElearningCartStats = () => {
  const { items, getItemCount, getCartTotal } = useElearningCartStore();
  
  return {
    itemCount: getItemCount(),
    totalAmount: getCartTotal(),
    isEmpty: items.length === 0,
    items
  };
};

/**
 * Hook pour les actions du panier
 */
export const useElearningCartActions = () => {
  const { addItem, removeItem, updateQuantity, clearCart, isItemInCart, getItem } = useElearningCartStore();
  
  return {
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    isItemInCart,
    getItem
  };
};

// ============================================================================
// FONCTIONS UTILITAIRES
// ============================================================================

/**
 * Formate le prix avec la devise
 */
export const formatPrice = (price: number, currency: string = 'FCFA'): string => {
  if (currency === 'FCFA' || currency === 'XAF') {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0
    }).format(price);
  }
  
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency
  }).format(price);
};

/**
 * Formate le niveau de difficulté
 */
export const formatDifficultyLevel = (level?: string): string => {
  const levels = {
    beginner: 'Débutant',
    intermediate: 'Intermédiaire',
    advanced: 'Avancé'
  };
  
  return level ? levels[level as keyof typeof levels] || level : '';
};

/**
 * Formate la durée du cours
 */
export const formatCourseDuration = (hours?: number): string => {
  if (!hours) return '';
  
  if (hours < 1) {
    return `${Math.round(hours * 60)} min`;
  } else if (hours === 1) {
    return '1 heure';
  } else {
    return `${hours} heures`;
  }
};

/**
 * Génère un résumé du panier pour l'affichage
 */
export const generateCartSummary = (items: ElearningCartItem[]) => {
  const totalItems = items.length;
  const totalAmount = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  const categories = [...new Set(items.map(item => item.category_name).filter(Boolean))];
  const instructors = [...new Set(items.map(item => item.instructor_name).filter(Boolean))];
  
  return {
    totalItems,
    totalAmount,
    categories,
    instructors,
    averagePrice: totalItems > 0 ? totalAmount / totalItems : 0
  };
};

/**
 * Valide un article avant de l'ajouter au panier
 */
export const validateCartItem = (item: Omit<ElearningCartItem, 'quantity'>): boolean => {
  return !!(
    item.course_id &&
    item.title &&
    typeof item.price === 'number' &&
    item.price >= 0 &&
    item.currency &&
    item.slug
  );
};

/**
 * Convertit un cours en article de panier
 */
export const courseToCartItem = (course: any): Omit<ElearningCartItem, 'quantity'> => {
  return {
    course_id: course.id,
    title: course.title,
    slug: course.slug,
    price: course.price || 0,
    currency: course.currency || 'FCFA',
    thumbnail_url: course.thumbnail_url,
    instructor_name: course.instructor?.name,
    instructor_avatar: course.instructor?.avatar_url,
    duration_hours: course.duration_hours,
    difficulty_level: course.difficulty_level,
    category_name: course.category?.name,
    category_color: course.category?.color
  };
};

// ============================================================================
// TYPES D'EXPORT
// ============================================================================

export type {
  ElearningCartItem,
  ElearningCartState
};
